import { useState, useEffect } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

// ── Navigation structure ───────────────────────────────────────────────────────

// Bottom tab bar — 4 primary destinations + "更多"
// Home = 自動交易；大盤預測（實驗記分板）降級到 /prediction（2026-07-13 審視）。
const BOTTOM_TABS = [
  { to: '/',           label: '自動交易', icon: '⭐', end: true  },
  { to: '/market',     label: '行情',  icon: '📊', end: false },
  { to: '/prediction', label: '大盤預測', icon: '🔮', end: false },
  { to: '/strategy',   label: '策略',  icon: '📈', end: false },
]

const navGroups = [
  {
    section: '行情',
    items: [
      { to: '/market',  label: '📊 市場行情' },
      { to: '/prediction', label: '🔮 大盤預測（實驗）' },
      { to: '/stock',   label: '🔍 個股查詢' },
    ],
  },
  {
    section: '交易',
    items: [
      { to: '/', label: '⭐ 自動交易', end: true },
      { to: '/market-sim', label: '🧪 開盤模擬' },
      { to: '/replay',    label: '📼 Tick 重放' },
      { to: '/accounts',  label: '💰 帳戶管理' },
      { to: '/futures',   label: '📋 期貨交易' },
      { to: '/ma-vol',    label: '📐 均線量能' },
    ],
  },
  {
    section: '分析',
    items: [
      { to: '/screener', label: '🚀 強勢股' },
      { to: '/kol',      label: '🎙️ KOL選股' },
      { to: '/open-book', label: '📒 開盤簿' },
      { to: '/topn',     label: '📊 Top-N 戰績' },
      { to: '/strategy', label: '📈 策略 & 回測' },
      { to: '/training-results', label: '🧠 訓練管理' },
      { to: '/experiment', label: '🧪 實驗完整性' },
    ],
  },
  {
    section: '系統',
    items: [
      { to: '/system',   label: '📟 系統狀態' },
      { to: '/settings', label: '⚙ 設定' },
    ],
  },
]

// ── NavBar ────────────────────────────────────────────────────────────────────

export default function NavBar() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => { setIsOpen(false) }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  async function handleLogout() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      {/* Mobile top bar — brand only on mobile, hamburger on desktop via CSS */}
      <div className="mobile-topbar">
        <button className="hamburger" onClick={() => setIsOpen(o => !o)} aria-label="toggle menu">
          <span /><span /><span />
        </button>
        <span className="mobile-brand">📈 自動交易</span>
      </div>

      {isOpen && <div className="sidebar-backdrop" onClick={() => setIsOpen(false)} />}

      <aside className={`sidebar${isOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          📈 自動交易
          <button className="sidebar-close" onClick={() => setIsOpen(false)} aria-label="close menu">✕</button>
        </div>

        <nav>
          {navGroups.map(group => (
            <div key={group.section} className="nav-group">
              <div className="nav-section-label">{group.section}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  className={({ isActive }) => isActive ? 'active' : ''}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">👤 {user?.username}</div>
          <button className="btn btn-secondary btn-sm sidebar-logout" onClick={handleLogout}>
            登出
          </button>
          <div style={{
            marginTop: 10, fontSize: 10,
            color: 'var(--text-muted)', fontFamily: 'monospace',
            lineHeight: 1.6, opacity: 0.7,
          }}>
            <div title="git commit hash">🔖 {__APP_GIT_HASH__}</div>
            <div title="build timestamp">{new Date(__APP_BUILD_TIME__).toLocaleString('zh-TW', {
              year: 'numeric', month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })}</div>
          </div>
        </div>
      </aside>

      {/* Mobile bottom tab bar */}
      <nav className="bottom-nav" aria-label="主選單">
        {BOTTOM_TABS.map(tab => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
          >
            <span className="bnav-icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </NavLink>
        ))}
        <button
          className={`bottom-nav-item${isOpen ? ' active' : ''}`}
          onClick={() => setIsOpen(o => !o)}
          aria-label="更多選單"
        >
          <span className="bnav-icon">☰</span>
          <span>更多</span>
        </button>
      </nav>
    </>
  )
}
